import { createSlice } from "@reduxjs/toolkit";

import { fetchActivityFeed, fetchByArea, fetchByUser } from "./actions";

export const initialState = {
  byArea: null,
  byUser: null,
  isLoading: false,
  error: null,
  filters: { range: "all", from: null, to: null, source: "all" },
  activityEvents: [],
  activityIsLoading: false,
  activityIsLoadingMore: false,
  activityError: null,
  activitySearch: "",
  activitySort: { field: "dateTime", direction: "DESC" },
  activityTotal: 0,
  activityFilteredTotal: 0,
  activityHasMore: false,
};

const DEFAULT_ERROR = "Failed to load usage stats";
const ACTIVITY_DEFAULT_ERROR = "Failed to load activity";

const usageStatsSlice = createSlice({
  name: "usageStats",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetError: (state) => {
      state.error = null;
      state.activityError = null;
    },
    setActivitySearch: (state, action) => {
      state.activitySearch = action.payload ?? "";
    },
    setActivitySort: (state, action) => {
      state.activitySort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchByArea.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchByArea.fulfilled, (state, action) => {
        state.isLoading = false;
        state.byArea = action.payload;
      })
      .addCase(fetchByArea.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message ?? DEFAULT_ERROR;
      })
      .addCase(fetchByUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchByUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.byUser = action.payload;
      })
      .addCase(fetchByUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message ?? DEFAULT_ERROR;
      })
      .addCase(fetchActivityFeed.pending, (state, action) => {
        const isFirstPage = (action.meta.arg?.offset ?? 0) === 0;
        state.activityIsLoading = isFirstPage;
        state.activityIsLoadingMore = !isFirstPage;
        state.activityError = null;
      })
      .addCase(fetchActivityFeed.fulfilled, (state, action) => {
        const { offset, rows, total, filteredTotal } = action.payload;
        state.activityIsLoading = false;
        state.activityIsLoadingMore = false;
        state.activityEvents = offset === 0 ? rows : [...state.activityEvents, ...rows];
        state.activityTotal = total;
        state.activityFilteredTotal = filteredTotal;
        state.activityHasMore = state.activityEvents.length < filteredTotal;
      })
      .addCase(fetchActivityFeed.rejected, (state, action) => {
        state.activityIsLoading = false;
        state.activityIsLoadingMore = false;
        state.activityError = action.error?.message ?? ACTIVITY_DEFAULT_ERROR;
      });
  },
});

export const { resetError, setActivitySearch, setActivitySort, setFilters } =
  usageStatsSlice.actions;

export default usageStatsSlice.reducer;
